import cn from 'classnames';
import React, { useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import updateMediaElementOperation, {
  MediaUpdateActionName,
} from 'editor/src/store/design/operation/updateMediaElementOperation';
import { MediaImage, MediaText, QrCodeFieldConfig, StructureIndex } from 'editor/src/store/design/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';

import {
  buildQrContentFromConfig,
  DEFAULT_QR_STYLE,
  QR_TYPE_FIELDS,
  QR_TYPE_LABELS,
  QR_TYPE_ORDER,
} from 'editor/src/lib/qr';
import { getVariableDataColumnValue } from 'editor/src/lib/variableData';
import useTr from 'editor/src/util/useTr';

import Button from 'editor/src/component/Button';

import FieldSourceRow, { QrTextField } from './FieldSourceRow';
import useQrPreview from './useQrPreview';

import styles from './index.module.scss';

import type { QrContentType } from 'editor/src/lib/qr';

interface Props {
  element: MediaImage;
  address: StructureIndex;
  onClose: () => void;
}

// The seller's per-type field mappings made earlier in this dialog session, kept so
// switching tabs back and forth does not lose work that has not been saved to a type
// the element isn't currently using.
type DraftsByType = Partial<Record<QrContentType, QrCodeFieldConfig[]>>;

const NO_COLUMNS: string[] = [];

function QrCodeConfig({ element, address, onClose }: Props) {
  const { t } = useTranslation();
  const tr = useTr();
  const dispatch = useDispatch();

  const spreads = useSelector((state) => state.design.designData?.spreads);
  // The code set's columns, offered beside the text fields as a source: one value per
  // printed unit instead of one per buyer.
  const variableData = useSelector((state) => state.design.designData?.variableData);
  // A QR varying per unit, from a column or a bound text, needs a host whose orders
  // draw a QR per unit, so neither is offered without this. A field already reading
  // one keeps showing it (see FieldSourceRow).
  const variableDataEnabled = useSelector((state) => getHostSetting(state, 'enableVariableDataFields'));
  const offeredColumns = variableDataEnabled ? variableData?.columns ?? NO_COLUMNS : NO_COLUMNS;
  const textFields = useMemo(() => {
    const fields: QrTextField[] = [];
    spreads?.forEach((spread) =>
      spread.pages.forEach((page) =>
        page.groups.media?.forEach((el) => {
          if (el.type === 'text' && !el.personalizationLocked) {
            fields.push({
              uuid: el.uuid,
              name: el.name || (el as MediaText).extra?.text || `${t('Text')} ${el.uuid}`,
              unavailable: !variableDataEnabled && !!(el as MediaText).extra?.variableColumn,
            });
          }
        }),
      ),
    );
    return fields;
  }, [spreads, t, variableDataEnabled]);

  // The dialog edits a local draft: nothing is written to the design (and no QR
  // asset is generated) until the seller confirms with Done. Closing with × discards.
  const initialQrCode = element.qrCode;
  const [activeType, setActiveType] = useState<QrContentType>(initialQrCode?.type ?? 'url');
  const [drafts, setDrafts] = useState<DraftsByType>({
    [initialQrCode?.type ?? 'url']: initialQrCode?.fields ?? [],
  });

  const fields = drafts[activeType] ?? [];

  const getLinkedText = (elementId: number): string | undefined => {
    let text: string | undefined;
    spreads?.forEach((spread) =>
      spread.pages.forEach((page) =>
        page.groups.media?.forEach((el) => {
          if (el.uuid === elementId && el.type === 'text') {
            text = (el as MediaText).extra?.text;
          }
        }),
      ),
    );
    return text;
  };

  const getColumnValue = (column: string) => getVariableDataColumnValue(variableData, column);

  const content = useMemo(
    () => buildQrContentFromConfig(activeType, fields, getLinkedText, getColumnValue),
    [activeType, fields, spreads, variableData],
  );
  const previewRef = useRef<HTMLDivElement>(null);
  useQrPreview(previewRef, { content, style: DEFAULT_QR_STYLE, size: 240 }, true);

  function updateFields(nextFields: QrCodeFieldConfig[]) {
    setDrafts((prev) => ({ ...prev, [activeType]: nextFields }));
  }

  function onSelectTab(type: QrContentType) {
    if (type !== activeType) {
      setActiveType(type);
    }
  }

  function onDone() {
    const nextQrCode = { type: activeType, fields };
    // Skip the no-op write: re-dispatching an identical config would clear `encoded`
    // and trigger a pointless QR regeneration + asset upload.
    const unchanged =
      initialQrCode &&
      initialQrCode.type === activeType &&
      JSON.stringify(initialQrCode.fields) === JSON.stringify(fields);
    if (!unchanged) {
      dispatch(
        updateMediaElementOperation(
          address,
          { qrCode: { ...nextQrCode, encoded: undefined } },
          MediaUpdateActionName.UNKNOWN,
          true,
        ),
      );
    }
    onClose();
  }

  function onFieldSourceChange(key: string, source: QrCodeFieldConfig['source'] | undefined) {
    const nextByKey = new Map(fields.map((field) => [field.key, field]));
    const existing = nextByKey.get(key);
    if (source) {
      nextByKey.set(key, { ...existing, key, source });
    } else {
      nextByKey.delete(key);
    }
    const orderedKeys = QR_TYPE_FIELDS[activeType].map((def) => def.key);
    const nextFields = orderedKeys.map((k) => nextByKey.get(k)).filter(Boolean) as QrCodeFieldConfig[];
    updateFields(nextFields);
  }

  const fieldByKey = new Map(fields.map((field) => [field.key, field]));

  return ReactDOM.createPortal(
    <div className={cn(styles.backdrop, 'cy-qr-config-backdrop')}>
      <div className={styles.dialog} role="dialog" aria-modal="true">
        <header className={styles.header}>
          <h2 className={styles.title}>{t('QR code')}</h2>
          <button
            type="button"
            className={cn(styles.close, 'cy-qr-config-close')}
            onClick={onClose}
            aria-label={t('Close')}
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          <div className={styles.controls}>
            <nav className={styles.typeTabs} aria-label={t('QR content type')}>
              {QR_TYPE_ORDER.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={cn(styles.typeTab, `cy-qr-tab-${type}`, { [styles.typeTabActive]: type === activeType })}
                  onClick={() => onSelectTab(type)}
                >
                  {t(QR_TYPE_LABELS[type])}
                </button>
              ))}
            </nav>

            <div className={styles.fieldsHint}>
              {offeredColumns.length
                ? tr(
                    'editor-qr-field-sources-hint-with-columns',
                    'Choose where each field comes from — a personalizable field, a fixed text, or a column of the code set (one value per printed unit).',
                  )
                : t('Choose where each field comes from — a personalizable field or a fixed text.')}
            </div>
            <div className={styles.fields}>
              {QR_TYPE_FIELDS[activeType].map((def) => (
                <FieldSourceRow
                  key={def.key}
                  def={def}
                  field={fieldByKey.get(def.key)}
                  textFields={textFields}
                  columns={offeredColumns}
                  onChange={(source) => onFieldSourceChange(def.key, source)}
                />
              ))}
            </div>
          </div>

          <aside className={styles.previewPane}>
            <div className={styles.previewFrame}>
              <div ref={previewRef} className={styles.preview} />
            </div>
          </aside>
        </div>

        <footer className={styles.footer}>
          <Button primary className="cy-qr-config-done" onClick={onDone}>
            {t('Done')}
          </Button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default QrCodeConfig;
